import { useEffect, useState } from "react";

import { Button, Sheet, useConfirm } from "@/ui";
import { useDeleteSpot, useUpdateSpot } from "@/features/queries";
import type { NetType, PlaceType, Spot } from "@/types/api";
import { useAuth } from "@/contexts/AuthContext";
import { noAutofill } from "@/lib/noAutofill";

interface Props {
  spot: Spot | null;
  onClose: () => void;
}

/** Bottom-sheet edit dialog opened from the map popup.
 *
 * Coordinates aren't editable here (rare operation, and the full map
 * picker lives on /admin/spots/:id/edit). Owners + admins can tweak
 * everything else; backend authorises per-user. */
export function SpotEditSheet({ spot, onClose }: Props) {
  return (
    <Sheet open={spot !== null} onClose={onClose} title="Редактировать площадку">
      {spot && <SpotEditForm spot={spot} onClose={onClose} />}
    </Sheet>
  );
}

function SpotEditForm({ spot, onClose }: { spot: Spot; onClose: () => void }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const update = useUpdateSpot();
  const del = useDeleteSpot();
  const confirm = useConfirm();

  const [name, setName] = useState(spot.name);
  const [address, setAddress] = useState(spot.address);
  const [placeType, setPlaceType] = useState<PlaceType>(spot.place_type);
  const [netType, setNetType] = useState<NetType>(spot.net_type ?? "metal");
  const [hasRoof, setHasRoof] = useState<boolean>(spot.has_roof ?? false);
  const [tablesCount, setTablesCount] = useState(String(spot.tables_count ?? 1));
  const [website, setWebsite] = useState(spot.website ?? "");
  const [phone, setPhone] = useState(spot.phone ?? "");

  useEffect(() => {
    // Reset form when switching between spots without unmounting.
    setName(spot.name);
    setAddress(spot.address);
    setPlaceType(spot.place_type);
    setNetType(spot.net_type ?? "metal");
    setHasRoof(spot.has_roof ?? false);
    setTablesCount(String(spot.tables_count ?? 1));
    setWebsite(spot.website ?? "");
    setPhone(spot.phone ?? "");
  }, [spot.id]);

  const onSave = async () => {
    try {
      await update.mutateAsync({
        id: spot.id,
        patch: {
          name: name.trim(),
          address: address.trim(),
          place_type: placeType,
          net_type: placeType === "outdoor_spot" ? netType : null,
          has_roof: placeType === "outdoor_spot" ? hasRoof : null,
          tables_count: placeType === "outdoor_spot" ? Number(tablesCount) : null,
          website: website.trim() || null,
          phone: phone.trim() || null,
        },
      });
      onClose();
    } catch {
      /* shown below */
    }
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: "Удалить площадку?",
      description: `«${spot.name}» будет удалена. Отменить нельзя.`,
      confirmLabel: "Удалить",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await del.mutateAsync(spot.id);
      onClose();
    } catch {
      /* shown below */
    }
  };

  const err = (update.error ?? del.error) as Error | null;

  return (
    <div
      style={{
        padding: "0 16px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <Field label="Название">
        <input
          style={inputStyle}
          value={name}
          onChange={(e) => setName(e.target.value)}
          {...noAutofill}
        />
      </Field>
      <Field label="Адрес">
        <input
          style={inputStyle}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          {...noAutofill}
        />
      </Field>

      <Field label="Тип">
        <select
          style={inputStyle}
          value={placeType}
          onChange={(e) => setPlaceType(e.target.value as PlaceType)}
          // Backend rejects non-admin changing to indoor_hall/store.
          disabled={!isAdmin && spot.place_type === "outdoor_spot"}
        >
          <option value="outdoor_spot">Уличная площадка</option>
          <option value="indoor_hall">Зал</option>
          <option value="store">Магазин</option>
        </select>
        {!isAdmin && spot.place_type === "outdoor_spot" && (
          <span className="caption" style={{ color: "var(--fg3)" }}>
            Сменить тип может только админ.
          </span>
        )}
      </Field>

      {placeType === "outdoor_spot" && (
        <>
          <Field label="Сетка">
            <select style={inputStyle} value={netType} onChange={(e) => setNetType(e.target.value as NetType)}>
              <option value="metal">Металлическая</option>
              <option value="regular">Обычная</option>
              <option value="none">Нет сетки</option>
            </select>
          </Field>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={hasRoof}
              onChange={(e) => setHasRoof(e.target.checked)}
            />
            <span className="body">Есть навес</span>
          </label>
          <Field label="Количество столов">
            <input
              style={inputStyle}
              type="number"
              value={tablesCount}
              min={1}
              max={50}
              onChange={(e) => setTablesCount(e.target.value)}
              {...noAutofill}
            />
          </Field>
        </>
      )}

      {placeType !== "outdoor_spot" && (
        <>
          <Field label="Сайт">
            <input
              style={inputStyle}
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              {...noAutofill}
            />
          </Field>
          <Field label="Телефон">
            <input
              style={inputStyle}
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              {...noAutofill}
            />
          </Field>
        </>
      )}

      {err && (
        <div
          style={{
            background: "rgba(223,63,64,0.1)",
            color: "var(--fg-danger)",
            padding: "8px 12px",
            borderRadius: "var(--r-md)",
            fontSize: 13,
          }}
        >
          {err.message}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <Button
          fullWidth
          onClick={onSave}
          disabled={update.isPending || del.isPending || !name.trim() || !address.trim()}
        >
          {update.isPending ? "Сохраняем…" : "Сохранить"}
        </Button>
        <Button fullWidth variant="ghost" onClick={onClose} disabled={update.isPending || del.isPending}>
          Отмена
        </Button>
      </div>
      <Button
        fullWidth
        variant="danger"
        onClick={onDelete}
        disabled={update.isPending || del.isPending}
      >
        {del.isPending ? "Удаляем…" : "Удалить площадку"}
      </Button>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  border: "1px solid var(--separator)",
  borderRadius: "var(--r-md)",
  padding: "10px 14px",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  background: "var(--bg)",
  color: "var(--fg1)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span className="label" style={{ color: "var(--fg2)" }}>{label}</span>
      {children}
    </div>
  );
}
